/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AddExternalCardInput,
  Address,
  AdvanceQualificationMethod,
  ButtonComponentActionResultTypenames,
  ButtonComponentV2,
  LendingPaymentSource,
  ProcessTypeInput,
} from '@varo/graphql-schema-types';
import {
  useLocalSearchParams,
  useRootNavigationState,
  useRouter,
} from 'expo-router';
import useExternalCard from 'features/moveMoney/screens/dcf/hooks/useAddExternalCard';
import useCancelTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useCancelTabapayTransfer';
import { useLogGQLErrorScreenAnalytics } from 'features/moveMoney/screens/dcf/hooks/useLogGQLErrorScreenAnalytics';
import { useTabaPayCardDetails } from 'features/moveMoney/screens/dcf/hooks/useTabaPayCardDetails';
import { useTabaPayLicense } from 'features/moveMoney/screens/dcf/hooks/useTabaPayLicense';
import { useThreeDsHandler } from 'features/moveMoney/screens/dcf/hooks/useThreeDsHandler';
import useThreeDsSdkWeb from 'features/moveMoney/screens/dcf/hooks/useThreeDsSdkWeb';
import {
  encryptCardData,
  encryptToken,
  getExternalAddressText,
  getFlowNameForAnalytics,
  getFormattedCardData,
  handleActionRoute,
  isProfileAddressSelected,
  isUserCancelled3DS,
  TabaPayEntryFlowType,
  TabaPayForm,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import { tabaPayCardFormSchema } from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPayForm.schema';
import { useTabaPayAddress } from 'features/moveMoney/screens/dcf/tabapay/providers/TabaPayContextProvider';
import useDeleteExternalCard from 'features/myVaro/screens/ExternalAccounts/hooks/useDeleteExternalCard';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { isWeb } from 'shared/constants/platform';
import {
  navigateBack,
  tryNavigateIgnoringUnmountedRoot,
} from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

export const useTabaPayDebitCardFundingForm = () => {
  const router = useRouter();
  const rootNavigationState = useRootNavigationState();
  const rootNavigationKey = rootNavigationState?.key;
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'paymentDetails',
  });

  const [selectedAddress, setSelectedAddress] = useState<Address>();
  const [loading, setLoading] = useState(false);
  const [isSuccessNavigationPending, setIsSuccessNavigationPending] =
    useState(false);
  const processedAddressCountRef = useRef(0);
  const [shouldDeleteCard, setShouldDeleteCard] = useState(false);
  const [actionRoute, setActionRoute] = useState('');
  const [isVerifyCardScreenShown, setIsVerifyCardScreenShown] =
    useState<boolean>(false);
  const {
    tabaPayEntryFlowType = TabaPayEntryFlowType.ExternalAccount,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    id,
    qualificationMethod,
    autoPaymentAgreementId,
    isBelieveDay0Flow,
  } = useLocalSearchParams<{
    tabaPayEntryFlowType?: TabaPayEntryFlowType;
    dueDate?: string;
    feeAmount?: string;
    amountId?: string;
    advanceAmount?: string;
    repaymentDate?: string;
    selectedAccountId?: string;
    paymentSource?: LendingPaymentSource;
    selectedAccountName?: string;
    id?: string; // advance account id for VA manual payment flow
    qualificationMethod: AdvanceQualificationMethod;
    autoPaymentAgreementId?: string; // string ex: "1,2,3"
    isBelieveDay0Flow?: string;
  }>();

  const {
    deleteExternalCard,
    isSuccess: deletedExternalCard,
    isLoading: removeCardLoading,
    errorModel: deleteExternalCardError,
  } = useDeleteExternalCard();

  const {
    addExternalCard,
    errorScreen,
    externalCardId,
    verifyCardActionScreen,
    resetCardData,
    resetError: resetExternalCardError,
  } = useExternalCard();
  const {
    handleThreeDS,
    errorScreenThreeDs,
    isLoading,
    initTransactionId,
    resetError: resetThreeDsError,
  } = useThreeDsHandler();
  const {
    startThreeDsWebFlow,
    isReady,
    errorScreenThreeDs: errorScreenWeb,
    isLoading: isLoadingWeb,
    initTransactionId: initTransactionIdWeb,
    setErrorScreen,
  } = useThreeDsSdkWeb();
  const {
    fullName,
    dropdownAddressList,
    title,
    description,
    disclaimer,
    defaultAddress,
    error: cardDetailsError,
    fetching: cardDetailsFetching,
  } = useTabaPayCardDetails();
  const { segmentClient } = useAnalyticsConfiguration();
  const { tabaPayKey, errorModel, fetching: loader } = useTabaPayLicense();

  const {
    cancelTabapayTransfer,
    isLoading: cancelTransferLoading,
    isSuccess,
    errorScreen: cancelTransferErrorScreen,
  } = useCancelTabapayTransfer();
  const {
    duplicateAddedAddress,
    userCardAddresses,
    setShouldRefreshCards,
    setUserCardAddresses,
  } = useTabaPayAddress();

  const flow = useVaroOnboardingFlow();

  const {
    refreshTiles,
    data: homeTilesData,
    fetching: homeFetching,
    error: homeError,
  } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.ACCOUNT_ACTION_TILE_ADD_MONEY_DDA,
  ]);

  const combinedErrorScreen =
    errorModel ||
    cardDetailsError ||
    errorScreen ||
    errorScreenThreeDs ||
    errorScreenWeb ||
    deleteExternalCardError ||
    cancelTransferErrorScreen;

  const shouldShowLoader =
    cardDetailsFetching ||
    loader ||
    isLoading ||
    isLoadingWeb ||
    loading ||
    removeCardLoading;

  const {
    control,
    getValues,
    trigger,
    formState: { errors, isValid },
    setValue,
    reset,
  } = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
    defaultValues: TabaPayForm,
    resolver: zodResolver(tabaPayCardFormSchema),
  });

  const checkAndDeleteCard = () => {
    if (externalCardId) {
      void deleteExternalCard({ externalCardId });
      // Clear card data to prevent verify screen from showing
      resetCardData();
    }
  };

  const cancelTransactionAndDeleteCard = () => {
    if (externalCardId) {
      const trimmedTransactionId = initTransactionId?.trim();
      if (trimmedTransactionId || initTransactionIdWeb) {
        setShouldDeleteCard(true);
        void cancelTabapayTransfer({
          transactionId: trimmedTransactionId || initTransactionIdWeb,
        });
      } else {
        void deleteExternalCard({ externalCardId });
      }
    } else {
      navigateBack();
    }
  };

  const flowName = getFlowNameForAnalytics(tabaPayEntryFlowType);
  const subcategory =
    isBelieveDay0Flow === 'true' ? 'dcf_believe_day_0_application' : flowName;

  const analyticsCommonAttributes = {
    subcategory,
    flow_name: flowName,
  };

  const expiryDateErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'expiration_date_validation',
      customer_message: errors.date?.message,
      ...analyticsCommonAttributes,
    },
  });

  const handleExpiryDateErrorTriggered = useCallback(() => {
    if (errors.date?.message) {
      expiryDateErrorEncountered();
    }
  }, [errors.date?.message, expiryDateErrorEncountered]);

  const cardNumberErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'card_number_validation',
      customer_message: errors.cardNumber?.message,
      ...analyticsCommonAttributes,
    },
  });

  const handleCardNumberInputErrorTriggered = useCallback(() => {
    if (errors.cardNumber?.message) {
      cardNumberErrorEncountered();
    }
  }, [cardNumberErrorEncountered, errors.cardNumber?.message]);

  const cvvErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'cvv_validation',
      customer_message: errors.cvv?.message,
      ...analyticsCommonAttributes,
    },
  });

  const handleCvvInputErrorTriggered = useCallback(() => {
    if (errors.cvv?.message) {
      cvvErrorEncountered();
    }
  }, [cvvErrorEncountered, errors.cvv?.message]);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'add-debit-card',
    customAttributes: analyticsCommonAttributes,
  });

  const submitOnPressEvent = usePressableAnalytics({
    analyticsId: 'addDebitCardSubmmited',
    customAttributes: analyticsCommonAttributes,
  });

  const addNewAddressOnPressEvent = usePressableAnalytics({
    analyticsId: 'addNewAddressClicked',
    customAttributes: analyticsCommonAttributes,
  });

  const isSameAddress = (a: Address, b: Address): boolean =>
    getExternalAddressText(a) === getExternalAddressText(b);

  const mergedAddresses = [...dropdownAddressList, ...userCardAddresses];

  const deduplicated = mergedAddresses.filter(
    (address, index, self) =>
      index === self.findIndex((a) => isSameAddress(a, address)),
  );

  const finalDropdownAddressList: Address[] = selectedAddress
    ? [
        selectedAddress,
        ...deduplicated.filter((a) => !isSameAddress(a, selectedAddress)),
      ]
    : deduplicated;

  useEffect(() => {
    if (duplicateAddedAddress) {
      setSelectedAddress(duplicateAddedAddress);
    }
  }, [duplicateAddedAddress]);

  useEffect(() => {
    setUserCardAddresses([]);
  }, [setUserCardAddresses]);

  useEffect(() => {
    if (isSuccess) {
      if (shouldDeleteCard && externalCardId) {
        checkAndDeleteCard();
      } else {
        navigateBack();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, shouldDeleteCard]);

  useEffect(() => {
    if (userCardAddresses.length === 0) {
      processedAddressCountRef.current = 0;
      return;
    }
    if (userCardAddresses.length > processedAddressCountRef.current) {
      const lastAddress = userCardAddresses[userCardAddresses.length - 1];
      if (
        !selectedAddress ||
        getExternalAddressText(selectedAddress) !==
          getExternalAddressText(lastAddress)
      ) {
        setSelectedAddress(lastAddress);
        setValue('address', getExternalAddressText(lastAddress));
      }
      processedAddressCountRef.current = userCardAddresses.length;
    }
  }, [userCardAddresses, selectedAddress, setValue]);

  useEffect(() => {
    if (defaultAddress) {
      const formatted = getExternalAddressText(defaultAddress);
      if (!selectedAddress?.addressLine1 && userCardAddresses.length === 0) {
        setValue('address', formatted);
        setSelectedAddress(defaultAddress);
      }
    }
  }, [defaultAddress, setValue, selectedAddress, userCardAddresses]);

  const goToAddCardSuccess = () => {
    // The native 3DS flow can resolve while the router root is still
    // (re)mounting; defer navigation to an effect gated on router readiness so
    // the user reaches the success screen instead of a stuck loader.
    if (!rootNavigationKey) {
      logBreadcrumb({
        message:
          'TabaPay add-card success navigation deferred: router root not ready',
        level: 'info',
        category: 'navigation',
      });
    }
    setIsSuccessNavigationPending(true);
  };

  useEffect(() => {
    if (!isSuccessNavigationPending || !rootNavigationKey) {
      return;
    }
    const didNavigate = tryNavigateIgnoringUnmountedRoot(() =>
      router.replace({
        pathname: `/${flow}/tabapay/add-card-success`,
        params: {
          tabaPayEntryFlowType: tabaPayEntryFlowType,
          dueDate,
          feeAmount,
          amountId,
          advanceAmount,
          repaymentDate,
          selectedAccountId,
          paymentSource,
          selectedAccountName,
          id,
          qualificationMethod,
          autoPaymentAgreementId,
        },
      }),
    );
    if (didNavigate) {
      setIsSuccessNavigationPending(false);
    }
  }, [
    isSuccessNavigationPending,
    rootNavigationKey,
    router,
    flow,
    tabaPayEntryFlowType,
    dueDate,
    feeAmount,
    amountId,
    advanceAmount,
    repaymentDate,
    selectedAccountId,
    paymentSource,
    selectedAccountName,
    id,
    qualificationMethod,
    autoPaymentAgreementId,
  ]);

  const shouldTrackErrorEvent =
    combinedErrorScreen && !combinedErrorScreen.analytics;

  useViewDidFocusScreenAnalyticsEvent(
    shouldTrackErrorEvent
      ? {
          key: 'add-debit-card-error',
          customAttributes: analyticsCommonAttributes,
        }
      : null,
  );

  useLogGQLErrorScreenAnalytics(
    combinedErrorScreen?.analytics,
    'Add Debit Card Form',
    flowName,
  );

  useEffect(() => {
    if (combinedErrorScreen) {
      setLoading(false);
    }
  }, [combinedErrorScreen]);

  const runThreeDS = async () => {
    if (!externalCardId) {
      return;
    }
    setLoading(true);
    setIsVerifyCardScreenShown(false);
    try {
      if (isWeb && isReady) {
        setIsVerifyCardScreenShown(false);
        const success = await startThreeDsWebFlow(
          ProcessTypeInput.CardRegistration,
          flowName,
          externalCardId,
        );

        if (success) {
          setLoading(true);
          setIsVerifyCardScreenShown(false);
          goToAddCardSuccess();
        }
      } else if (isWeb && !isReady) {
        setErrorScreen({
          screenType: ErrorScreenType.Error,
          messageHeadline: translatedText('errorMessage.threeDSErrorTitle'),
          messageBody: translatedText('errorMessage.tryAgain'),
          primaryButton: undefined,
        });
        void segmentClient.logEvent(
          'Add Debit Card Songbird Script Error Encountered',
          {
            category: 'link_external_debit_card',
            subcategory: 'add_card',
            money_flow_direction: 'va_day_0',
            error_code: translatedText('errorMessage.tryAgain'),
            error_title: translatedText('errorMessage.threeDSErrorTitle'),
          },
        );
      } else if (!isWeb) {
        setIsVerifyCardScreenShown(false);
        const threeDsResult = await handleThreeDS(
          ProcessTypeInput.CardRegistration,
          flowName,
          externalCardId,
        );
        if (threeDsResult) {
          setLoading(true);
          setIsVerifyCardScreenShown(false);
          goToAddCardSuccess();
        }
      }
    } catch (error) {
      logErrorWithContext('ThreeDs error:' + error, {
        featureArea: FeatureArea.LINK_EXTERNAL_DEBIT_CARD_,
        responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
        severity: ErrorSeverity.CRITICAL,
      });
    }
  };

  useEffect(() => {
    if ((homeTilesData?.tiles || homeError) && !homeFetching) {
      if (externalCardId && verifyCardActionScreen) {
        // Only show verify screen if we have valid card data
        setIsVerifyCardScreenShown(true);
      }
    }
  }, [
    homeTilesData,
    cardDetailsError,
    cardDetailsFetching,
    homeError,
    homeFetching,
    flow,
    externalCardId,
    verifyCardActionScreen,
  ]);

  useEffect(() => {
    if (externalCardId) {
      refreshTiles();
    }
  }, [externalCardId, refreshTiles]);

  const handleSubmitAction = async () => {
    submitOnPressEvent();
    setLoading(true);
    const { cardNumber, date, cvv } = getValues();
    await trigger();
    try {
      if (isValid && !(Object.entries(errors).length > 0)) {
        const formattedCard = getFormattedCardData(cardNumber, date, cvv);
        let token = '';
        if (tabaPayKey && formattedCard) {
          if (isWeb) {
            token = await encryptToken(tabaPayKey, formattedCard);
          } else {
            token = await encryptCardData(tabaPayKey, formattedCard);
          }
          const addressInput: AddExternalCardInput = {
            encryptedData: token,
            cardHolderName: fullName ?? '',
            isProfileAddress: isProfileAddressSelected(
              selectedAddress,
              defaultAddress,
            ),
            address: {
              addressLine1: selectedAddress?.addressLine1 ?? '',
              addressLine2: selectedAddress?.addressLine2 ?? '',
              city: selectedAddress?.city ?? '',
              state: selectedAddress?.state ?? '',
              zip: selectedAddress?.zip ?? '',
              country: selectedAddress?.country ?? '',
            },
          };
          await addExternalCard(addressInput);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      logErrorWithContext('addExternalCard error:' + error, {
        featureArea: FeatureArea.LINK_EXTERNAL_DEBIT_CARD_,
        responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
        severity: ErrorSeverity.CRITICAL,
      });
    }
  };

  const handleCloseError = async () => {
    setShouldRefreshCards(true);
    if (
      (isUserCancelled3DS(combinedErrorScreen!) &&
        errorScreenWeb?.processType === ProcessTypeInput.Transaction) ||
      errorScreenThreeDs?.processType === ProcessTypeInput.Transaction
    ) {
      await cancelTabapayTransfer(
        { transactionId: initTransactionId?.trim() || initTransactionIdWeb },
        flowName,
      );
    } else {
      checkAndDeleteCard();
    }
    clearAllErrors();
  };

  const handleErrorPrimaryButtonOnPress = () => {
    // A "Continue" press on a general (non-3DS) add-card error resubmits the
    // form with the card details the user already entered. clearAllErrors()
    // resets all form fields, so snapshot the card fields first and restore
    // them afterward — otherwise the resubmit reads back blank values.
    const primaryAction = combinedErrorScreen?.primaryButton?.buttonOnClick;
    const isGeneralErrorResubmit =
      primaryAction?.__typename ===
        ButtonComponentActionResultTypenames.SDContinueClientFlowAction &&
      !!errorScreen &&
      !errorScreenThreeDs &&
      !errorScreenWeb;
    const preservedCardValues = isGeneralErrorResubmit
      ? getValues()
      : undefined;
    clearAllErrors();
    if (preservedCardValues) {
      setValue('cardNumber', preservedCardValues.cardNumber);
      setValue('date', preservedCardValues.date);
      setValue('cvv', preservedCardValues.cvv);
    }
    if (combinedErrorScreen?.primaryButton?.buttonText) {
      handleErrorButtonPress(combinedErrorScreen.primaryButton);
    } else {
      checkAndDeleteCard();
      navigateToTabapayForm();
    }
  };

  const handleErrorButtonPress = (button?: ButtonComponentV2) => {
    if (!button?.buttonOnClick) {
      return;
    } else if (!button?.buttonText) {
      checkAndDeleteCard();
    }

    const action = button.buttonOnClick;
    switch (action.__typename) {
      case ButtonComponentActionResultTypenames.RouteNavigationAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        setActionRoute(action.route);
        checkAndDeleteCard();
        break;

      case ButtonComponentActionResultTypenames.SDContinueClientFlowAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        // Retry with the same card: a 3DS error should re-launch the 3DS
        // challenge, while a general add-card error should resubmit the form.
        if (errorScreenThreeDs || errorScreenWeb) {
          void runThreeDS();
        } else if (errorScreen) {
          void handleSubmitAction();
        }
        break;

      case ButtonComponentActionResultTypenames.SDBackNavigationAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        setShouldRefreshCards(true);
        if (button.id === 'EXIT_BUTTON') {
          if (
            errorScreenWeb?.processType === ProcessTypeInput.Transaction ||
            errorScreenThreeDs?.processType === ProcessTypeInput.Transaction
          ) {
            void cancelTabapayTransfer(
              {
                transactionId:
                  initTransactionId?.trim() || initTransactionIdWeb,
              },
              flowName,
            );
          } else {
            navigateBack();
          }
        } else if (externalCardId && button.id === 'ADD_DIFFERENT_CARD') {
          cancelTransactionAndDeleteCard();
        } else {
          navigateBack();
        }

        break;

      default:
        navigateToTabapayForm();
    }
  };

  const navigateToTabapayForm = () => {
    router.replace({
      pathname: `/${flow}/tabapay/dcf-tabapay-form`,
      params: { tabaPayEntryFlowType },
    });
  };

  useEffect(() => {
    if (actionRoute) {
      handleActionRoute(actionRoute, flow, tabaPayEntryFlowType);
    }
  }, [actionRoute, deletedExternalCard, flow, tabaPayEntryFlowType]);

  const clearAllErrors = () => {
    setErrorScreen?.(null); // Clear errorScreenWeb from useThreeDsSdkWeb
    resetThreeDsError(); // Clear errorScreenThreeDs from useThreeDsHandler
    resetExternalCardError(); // Clear errorScreen from useAddExternalCard
    resetCardData();
    reset(); // Reset form fields to initial empty state
    // Re-sync address with selectedAddress, since reset() clears the form
    // field but not selectedAddress, which would leave "Next" disabled.
    if (selectedAddress) {
      setValue('address', getExternalAddressText(selectedAddress));
    }
  };

  return {
    isVerifyCardScreenShown,
    title,
    description,
    disclaimer,
    addNewAddressOnPressEvent,
    control,
    cancelTransferLoading,
    verifyCardActionScreen,
    shouldShowLoader,
    setIsVerifyCardScreenShown,
    runThreeDS,
    combinedErrorScreen,
    setShouldRefreshCards,
    translatedText,
    handleErrorButtonPress,
    handleErrorPrimaryButtonOnPress,
    errors,
    selectedAddress,
    fullName,
    finalDropdownAddressList,
    isValid,
    handleSubmitAction,
    setSelectedAddress,
    transactionId: initTransactionId?.trim() || initTransactionIdWeb,
    cancelTabapayTransfer,
    errorScreenWeb,
    errorScreenThreeDs,
    handleCardNumberInputErrorTriggered,
    handleExpiryDateErrorTriggered,
    handleCvvInputErrorTriggered,
    trigger,
    checkAndDeleteCard,
    externalCardId,
    clearAllErrors,
    navigateToTabapayForm,
    handleCloseError,
    getValues,
    setValue,
  };
};
